import { useQuery } from '@tanstack/react-query';
import { LIST_BADGE_LIFECYCLE_STATE } from '@terra/shared-dto-types';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import {
  buildNeedsAttentionItems,
  type NeedsAttentionItem,
} from '../../components/dashboard/needs-attention-items';
import { countFindingsForTarget } from '../api/target-findings-api';
import { useAuthenticationInfo } from './useAuthenticationInfo';
import { useExecutionStateDistribution } from './useExecutionStateDistribution';
import { useSignalsStageCounts } from './useSignalsStageCounts';
import { useThreadsWaitingForInput } from './useThreadsWaitingForInput';

export function useNeedsAttention({
  applicationId,
  targetType,
}: {
  applicationId: string;
  targetType: TargetTypeType;
}): { items: NeedsAttentionItem[]; isLoading: boolean } {
  const { data: signals, isLoading: isSignalsLoading } =
    useSignalsStageCounts(applicationId);
  const { data: executions, isLoading: isExecutionsLoading } =
    useExecutionStateDistribution(applicationId);
  const { data: authentication, isLoading: isAuthenticationLoading } =
    useAuthenticationInfo(applicationId);
  const threadsWaitingForInput = useThreadsWaitingForInput(applicationId);
  const { data: unpublishedFindings, isLoading: isFindingsLoading } = useQuery({
    queryKey: ['unpublished-findings-count', applicationId, targetType],
    queryFn: () =>
      countFindingsForTarget({
        targetId: applicationId,
        targetType,
        filter: {
          lifecycleState: LIST_BADGE_LIFECYCLE_STATE,
          isPublished: false,
        },
      }),
    enabled: applicationId.length > 0,
    staleTime: 30_000,
  });

  return {
    isLoading:
      isSignalsLoading ||
      isExecutionsLoading ||
      isAuthenticationLoading ||
      isFindingsLoading,
    items: buildNeedsAttentionItems({
      counts: {
        failedCredentials: authentication?.credentials.failed ?? 0,
        waitingForInput: threadsWaitingForInput.length,
        blockedExecutions: executions?.blocked ?? 0,
        pausedExecutions: executions?.stopped ?? 0,
        blockedSignals: signals?.blocked ?? 0,
        signalsNeedingReview: signals?.byStage.NEEDS_REVIEW ?? 0,
        unpublishedFindings: unpublishedFindings ?? 0,
        failedExecutions: executions?.failed ?? 0,
      },
      waitingForInputThreadId: threadsWaitingForInput[0] ?? null,
    }),
  };
}
